import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form } from "react-final-form";
import { AuthorizationResponse } from "paypal-checkout-components";
import { ApplePayPayload } from "braintree-web";
import { ThemeContext } from "styled-components";
import { TenantContext } from "v3/context/tenant";
import { CheckoutContext } from "v3/context/checkout";
import { LocalizationContext } from "v3/context/localization";
import { BraintreeContext } from "v3/context/braintree";
import { PaymentContext } from "v3/context/payment";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { Button } from "@pepdirect/v3/ui/Button";
import { Checkbox } from "@pepdirect/v3/ui/Checkbox";
import { PlusIconSvg } from "@pepdirect/v3/ui/icons";
import { OnlyAllocationAllowedAlert } from "v3/components/checkout/alerts/OnlyAllocationAllowedAlert";
import { SectionHeading } from "v3/components/checkout/SectionHeading";
import { AddressDetails } from "v3/components/orderDetails/AddressDetails";
import { BillingAddressForm } from "v3/components/forms/BillingAddressForm";
import { CreditCardDetailsForm } from "v3/components/forms/CreditCardDetailsForm";
import { PayPalPayment } from "v3/components/payment/paypal/PayPalPayment";
import { PaymentTypeSelector } from "v3/components/payment/PaymentTypeSelector";
import { SavedPaymentMethods } from "v3/components/checkout/SavedPaymentMethods";
import { ApplePayPayment } from "v3/components/payment/ApplePay/ApplePayPayment";
import { PoNumberInput } from "v3/components/checkout/PoNumberInput";
import {
  AllowedPaymentMethod,
  Credit,
  useAvailablePaymentMethodsLazyQuery,
  useFinalizePriceMutation,
  useSavePaymentMutation,
  useSavePaymentOptionsMutation,
} from "v3/services/graphql/generated";
import { getIsOrderMoreThanAllocationAmount } from "v3/helpers/checkout";
import { getHostedFieldsTokenizePayload } from "v3/services/creditCard";
import { convertAddressFromFormToInput } from "v3/helpers/address";
import {
  filterAndSortPaymentMethods,
  isApplePaySupported,
} from "v3/helpers/payment";
import {
  PaymentAndBillingFormValues,
  paymentValidation,
} from "v3/helpers/validation";
import {
  BraintreeErrorFieldsInvalid,
  EMBEDDED_GIFT_CARD_TYPES,
  SavedPaymentMethodsType,
} from "v3/types/payment";
import { braintreeErrors } from "v3/constants/payment";
import { StyledFieldSet } from "@pepdirect/v3/ui/fields/base";
import {
  StyledAddButton,
  StyledCheckboxContainer,
  StyledCoversFullAmount,
  StyledHeading,
  StyledLabel,
  StyledNewPaymentForm,
  StyledPaymentTypeSelector,
  StyledSavedPaymentMethods,
  StyledSpinner,
  StyledTitle,
} from "./index.styled";
import { logAddShippingInfo } from "v3/services/analytics";
import { Alert } from "@pepdirect/v3/ui/Alert";
import { UnavailableAllocationToCanadaAlert } from "v3/components/checkout/alerts/UnavailableAllocationToCanada";

interface BraintreePaymentStepProps {
  continueToNextStep: () => void;
}

export const BraintreePaymentStep: FC<BraintreePaymentStepProps> = ({
  continueToNextStep,
}) => {
  const { checkoutSession, isLoading, setCheckoutSession, setIsLoading } =
    useContext(CheckoutContext);
  const { isStateRequired } = useContext(TenantContext);
  const { client, hostedFields, setClient } = useContext(BraintreeContext);
  const {
    availablePaymentMethods,
    clearPaymentDataOnEdit,
    handleSelectApplePay,
    handleSelectInvoice,
    handleSelectNewCreditCard,
    handleSelectNewPayPal,
    handleSelectSavedCreditCard,
    handleSelectSavedPayPal,
    handleSelectSavedAllocatedCredit,
    newApplePayResponse,
    newPayPalResponse,
    paymentNonce,
    paymentType,
    savePaymentInput,
    setAvailablePaymentMethods,
    setNewApplePayResponse,
    setNewCreditCardBraintreeResponse,
    setNewPayPalResponse,
  } = useContext(PaymentContext);
  const { clearAllErrors, handleCheckoutErrors, setErrorMsg } =
    useContext(CheckoutErrorContext);
  const theme = useContext(ThemeContext);
  const { localization } = useContext(LocalizationContext);

  const {
    headingText = "Select a payment method",
    addPaymentButtonText = "Add a new payment method",
    addPaymentHeadingText = "Add a New Payment Method",
    billingAddressHeadingText = "Billing address",
    billingSameAsShippingText = "Billing address is the same as shipping address",
    shippingAddressHeadingText = "Shipping Address",
    continueToReviewButtonText = "Continue To Review",
    giftCardCoversFullAmountTitle = "Your gift card covers the full amount of your order",
    giftCardCoversFullAmountDescription = "Please select a payment method, but it will not be charged unless your order changes and the gift card is no longer able to cover the full amount.",
  } = localization?.checkout?.paymentStep || {};

  const { contactInfo, customerPo, giftInfo, id, user, price } =
    checkoutSession || {};

  const [isBillingEqualShipping, setIsBillingEqualShipping] = useState(
    !giftInfo
  );
  const [saveCardForLater, setSaveCardForLater] = useState(false);
  const [paymentStepReady, setPaymentStepReady] = useState(false);
  const [hasLoggedEvent, setHasLoggedEvent] = useState(false);
  const [sortedPaymentMethods, setSortedPaymentMethods] =
    useState<SavedPaymentMethodsType>([]);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [addNewPaymentDisabled, setAddNewPaymentDisabled] = useState(false);

  const [finalizePrice] = useFinalizePriceMutation();
  const [savePayment] = useSavePaymentMutation();
  const [savePaymentOptions] = useSavePaymentOptionsMutation();
  const [getAvailablePaymentMethods] = useAvailablePaymentMethodsLazyQuery();

  const isInvoiceUser = !!checkoutSession?.activeAccount?.isInvoiced;

  const validCreditCardTypes =
    availablePaymentMethods?.creditCard?.validCardTypes;

  const { shippingAddress, phone } = contactInfo || {};
  const isGuest = !user;

  const onlyAllowAllocation = shippingAddress?.countryCode === "CA";

  useEffect(() => {
    if (checkoutSession && !hasLoggedEvent) {
      setHasLoggedEvent(true);
      logAddShippingInfo(
        checkoutSession.cart,
        checkoutSession.shippingMethodConfig?.name.toLowerCase() || null
      );
    }
  }, [checkoutSession, hasLoggedEvent]);

  useEffect(() => {
    if (isBillingEqualShipping === null) {
      setIsBillingEqualShipping(!giftInfo);
    }
  }, [isBillingEqualShipping, giftInfo, setIsBillingEqualShipping]);

  const allowedPaymentMethods = useMemo(
    () => availablePaymentMethods?.allowedPaymentMethods || [],
    [availablePaymentMethods]
  );

  const isPayPalAllowed = useMemo(
    () => allowedPaymentMethods.includes(AllowedPaymentMethod.Paypal),
    [allowedPaymentMethods]
  );

  const isApplePayAllowed = useMemo(
    () =>
      allowedPaymentMethods.includes(AllowedPaymentMethod.ApplePay) &&
      isApplePaySupported(),
    [allowedPaymentMethods]
  );

  const isInvoiceAllowed = useMemo(
    () => allowedPaymentMethods.includes(AllowedPaymentMethod.Invoice),
    [allowedPaymentMethods]
  );

  const isOrderMoreThanAllocationAmount = useMemo(
    () =>
      getIsOrderMoreThanAllocationAmount({
        paymentMethods: sortedPaymentMethods,
        totalPrice: price?.total,
      }),
    [price?.total, sortedPaymentMethods]
  );

  const hasActiveAllocation = useMemo(
    () =>
      sortedPaymentMethods.find(
        (paymentMethod) =>
          paymentMethod.__typename === "Credit" &&
          paymentMethod.type === "allocation"
      ),
    [sortedPaymentMethods]
  );

  const hasSavedPaymentMethods = !!sortedPaymentMethods?.length;

  useEffect(() => {
    const getPaymentMethods = async () => {
      try {
        const { data, error } = await getAvailablePaymentMethods({
          variables: { id },
        });
        if (error) throw error;

        const { availablePaymentMethods } = data?.checkoutSession || {};

        if (availablePaymentMethods) {
          setAvailablePaymentMethods(availablePaymentMethods);
          const paymentMethodsSorted = filterAndSortPaymentMethods(
            availablePaymentMethods
          );
          setSortedPaymentMethods(paymentMethodsSorted);
          if (!paymentMethodsSorted.length) {
            handleSelectNewCreditCard({ paymentNonce: "" });
            setShowNewPaymentForm(true);
          }
        }
      } catch (e) {
        handleCheckoutErrors(e);
      } finally {
        setPaymentStepReady(true);
      }
    };
    if (!paymentStepReady) {
      getPaymentMethods();
    } else {
      setPaymentStepReady(true);
    }
  }, [
    availablePaymentMethods,
    getAvailablePaymentMethods,
    handleCheckoutErrors,
    handleSelectNewCreditCard,
    id,
    paymentStepReady,
    setAvailablePaymentMethods,
  ]);

  // pre-select the first saved payment method on load
  useEffect(() => {
    if (
      !isInvoiceAllowed &&
      hasSavedPaymentMethods &&
      availablePaymentMethods
    ) {
      const allocationCreditPaymentMethod = sortedPaymentMethods.find(
        (paymentMethod): paymentMethod is Credit =>
          paymentMethod.__typename === "Credit"
      );
      if (onlyAllowAllocation) {
        if (allocationCreditPaymentMethod && !isOrderMoreThanAllocationAmount) {
          handleSelectSavedAllocatedCredit({
            id: allocationCreditPaymentMethod.id,
          });
        } else {
          // If allocation itself is not present, clear the existing selection.
          // Clearing the selection is needed for following reason -
          // When user adds a US address on the Shipping step, and goes to Payment step, selects a payment method like Credit Card or Paypal,
          // comes back to Shipping step, adds a Canada address and again goes to Payment step, then the previously selected payment
          // method remained selected even if it is disabled for Canada. Hence calling `clearPaymentDataOnEdit()` in that case to
          // manually clear the selection is needed.
          clearPaymentDataOnEdit();
        }
        return;
      }
      const firstPaymentMethod = sortedPaymentMethods[0];
      // always pre-select allocation if available and enough to cover total
      if (allocationCreditPaymentMethod && !isOrderMoreThanAllocationAmount) {
        handleSelectSavedAllocatedCredit({
          id: allocationCreditPaymentMethod.id,
        });
      } else if (firstPaymentMethod.__typename === "CreditCard") {
        const { token } = firstPaymentMethod;
        handleSelectSavedCreditCard({
          billingAddress: firstPaymentMethod.billingAddress,
          token,
        });
      } else if (firstPaymentMethod.__typename === "PaypalAccount") {
        const { token } = firstPaymentMethod;
        handleSelectSavedPayPal({
          token,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePaymentMethods, clearPaymentDataOnEdit, onlyAllowAllocation]);

  // pre-select invoice if allowed
  // if invoice, it is the only payment method allowed
  useEffect(() => {
    if (isInvoiceAllowed) {
      setAddNewPaymentDisabled(true);
      handleSelectInvoice();
    }
  }, [isInvoiceAllowed, handleSelectInvoice]);

  useEffect(() => {
    if (savePaymentInput?.savedCreditCard || savePaymentInput?.paypal?.token)
      setShowNewPaymentForm(false);
  }, [savePaymentInput]);

  useEffect(() => {
    if (!client) setClient();
  }, [client, setClient]);

  const orderHasSubscription = useMemo(() => {
    return checkoutSession?.price.lineItems.some(
      (item) =>
        item.__typename === "LineItem" && item.subscriptionIntervalInDays
    );
  }, [checkoutSession?.price.lineItems]);

  const giftCardCoversFullAmount = useMemo(() => {
    return (
      checkoutSession?.giftCards?.some(
        (item) => item?.type === EMBEDDED_GIFT_CARD_TYPES.BALANCE
      ) && checkoutSession.price.total === 0
    );
  }, [checkoutSession?.giftCards, checkoutSession?.price.total]);

  const isContinueButtonDisabled = useCallback(
    (isFormValid: boolean) => {
      if (!showNewPaymentForm) return !savePaymentInput; // If no saved option is selected, disable the continue button
      if (paymentType === AllowedPaymentMethod.CreditCard) return !isFormValid;
      if (paymentType === AllowedPaymentMethod.Paypal)
        return !newPayPalResponse;
      if (paymentType === AllowedPaymentMethod.ApplePay)
        return !newApplePayResponse;
    },
    [
      showNewPaymentForm,
      paymentType,
      newPayPalResponse,
      newApplePayResponse,
      savePaymentInput,
    ]
  );

  const handleSelectNewPaymentType = (paymentType: AllowedPaymentMethod) => {
    const emptyNonce = { paymentNonce: "" };
    if (paymentType === AllowedPaymentMethod.CreditCard) {
      handleSelectNewCreditCard(emptyNonce);
    } else if (paymentType === AllowedPaymentMethod.Paypal) {
      handleSelectNewPayPal(emptyNonce);
    } else if (paymentType === AllowedPaymentMethod.ApplePay) {
      handleSelectApplePay(emptyNonce);
    }
  };

  const handleShowNewPaymentForm = () => {
    handleSelectNewCreditCard({ paymentNonce: "" });
    setShowNewPaymentForm(true);
  };

  const handleValidation = (values: PaymentAndBillingFormValues) => {
    if (showNewPaymentForm && paymentType === AllowedPaymentMethod.CreditCard) {
      return paymentValidation({
        values,
        isBillingEqualShipping: !!isBillingEqualShipping,
        isStateRequired,
        localization: localization?.forms?.validation,
      });
    }
    return {};
  };

  const onPaypalApprove = async (data: AuthorizationResponse) => {
    setNewPayPalResponse(data);
    handleSelectNewPayPal({
      paymentNonce: data.nonce,
    });
  };

  const onApplePayApprove = async (data: ApplePayPayload) => {
    setNewApplePayResponse(data);
    handleSelectApplePay({
      paymentNonce: data.nonce,
    });
  };

  const onSubmit = async (values: PaymentAndBillingFormValues) => {
    clearAllErrors();
    setIsLoading(true);
    const { billingAddress, payment, poNumber } = values;

    // set PO number if entered, but also clear it if unset
    if (poNumber || (customerPo && !poNumber)) {
      try {
        await savePaymentOptions({
          variables: {
            id,
            input: { customerPo: poNumber || "" },
          },
        });
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    // get nonce (token) for new credit cards
    if (
      hostedFields &&
      paymentType === "CREDIT_CARD" &&
      !savePaymentInput?.savedCreditCard
    ) {
      const tokenizeBillingAddress = isBillingEqualShipping
        ? shippingAddress
        : billingAddress;

      // something went wrong
      if (!payment.cardholderName || !tokenizeBillingAddress) return null;

      let payload;

      try {
        payload = await getHostedFieldsTokenizePayload(
          hostedFields,
          tokenizeBillingAddress,
          payment.cardholderName
        );
      } catch (tokenizeErr) {
        setErrorMsg(
          braintreeErrors(tokenizeErr as BraintreeErrorFieldsInvalid)
        );
        setIsLoading(false);
        return;
      }

      setNewCreditCardBraintreeResponse(payload);
      handleSelectNewCreditCard({ paymentNonce: payload.nonce });

      try {
        await savePayment({
          variables: {
            id,
            input: {
              creditCard: {
                isBillingEqualShipping,
                saveCardForLater,
                ...(!isBillingEqualShipping &&
                  billingAddress && {
                    billingAddress:
                      convertAddressFromFormToInput(billingAddress),
                  }),
              },
            },
          },
        });
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    } else {
      // something went wrong
      if (!savePaymentInput) return null;
      try {
        await savePayment({
          variables: {
            id,
            input: savePaymentInput,
          },
        });
      } catch (e) {
        handleCheckoutErrors(e);
        setIsLoading(false);
        return;
      }
    }

    try {
      const { data } = await finalizePrice({
        variables: {
          id,
        },
      });
      if (data?.finalizePrice) {
        setCheckoutSession(data.finalizePrice);
      }
    } catch (e) {
      handleCheckoutErrors(e);
      setIsLoading(false);
      return;
    }

    continueToNextStep();
    setIsLoading(false);
  };
  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      validate={handleValidation}
      render={({ handleSubmit, valid }) => (
        <form onSubmit={handleSubmit}>
          <StyledFieldSet disabled={isLoading}>
            <SectionHeading>Payment</SectionHeading>
            <StyledHeading size="xs" level="h3">
              {headingText}
            </StyledHeading>
            {giftCardCoversFullAmount && (
              <StyledCoversFullAmount>
                <Alert title={giftCardCoversFullAmountTitle}>
                  {giftCardCoversFullAmountDescription}
                </Alert>
              </StyledCoversFullAmount>
            )}
            {onlyAllowAllocation && (
              <>
                {hasActiveAllocation ? (
                  <OnlyAllocationAllowedAlert />
                ) : (
                  <UnavailableAllocationToCanadaAlert />
                )}
              </>
            )}
            {paymentStepReady && availablePaymentMethods ? (
              <>
                {hasSavedPaymentMethods && (
                  <>
                    <StyledSavedPaymentMethods>
                      <SavedPaymentMethods
                        onlyAllowAllocation={onlyAllowAllocation}
                        totalPrice={price?.total}
                        closeNewPaymentForm={() => setShowNewPaymentForm(false)}
                        paymentMethods={sortedPaymentMethods}
                        selectedToken={
                          paymentNonce ||
                          savePaymentInput?.paypal?.token ||
                          savePaymentInput?.savedCreditCard?.token
                        }
                      />
                    </StyledSavedPaymentMethods>
                    {!showNewPaymentForm &&
                      !addNewPaymentDisabled &&
                      !onlyAllowAllocation && (
                        <StyledAddButton>
                          <Button
                            onClick={handleShowNewPaymentForm}
                            type="text"
                          >
                            <PlusIconSvg color={theme.button.text.color} />
                            <span>{addPaymentButtonText}</span>
                          </Button>
                        </StyledAddButton>
                      )}
                  </>
                )}
                {showNewPaymentForm && !onlyAllowAllocation && (
                  <StyledNewPaymentForm>
                    {hasSavedPaymentMethods && (
                      <StyledHeading level="h2" size="s">
                        {addPaymentHeadingText}
                      </StyledHeading>
                    )}
                    <StyledPaymentTypeSelector>
                      <PaymentTypeSelector
                        allowedPaymentMethods={allowedPaymentMethods}
                        setSelectedPaymentType={handleSelectNewPaymentType}
                        selectedPaymentType={paymentType}
                        validCreditCardTypes={validCreditCardTypes}
                      />
                    </StyledPaymentTypeSelector>
                    {paymentType === AllowedPaymentMethod.CreditCard && (
                      <>
                        <CreditCardDetailsForm
                          isGuest={isGuest}
                          saveCardForLater={saveCardForLater}
                          setSaveCardForLater={setSaveCardForLater}
                          orderHasSubscription={orderHasSubscription}
                        />
                        <StyledHeading level="h3" size="xs">
                          {billingAddressHeadingText}
                        </StyledHeading>
                        <StyledCheckboxContainer>
                          <StyledLabel>
                            <Checkbox
                              checked={isBillingEqualShipping}
                              onChange={() =>
                                setIsBillingEqualShipping(
                                  !isBillingEqualShipping
                                )
                              }
                            />
                            {billingSameAsShippingText}
                          </StyledLabel>
                        </StyledCheckboxContainer>
                        {isBillingEqualShipping ? (
                          <>
                            <StyledTitle>
                              {shippingAddressHeadingText}
                            </StyledTitle>
                            <AddressDetails
                              address={shippingAddress || null}
                              phone={phone}
                            />
                          </>
                        ) : (
                          <BillingAddressForm />
                        )}
                      </>
                    )}
                    {isPayPalAllowed &&
                      paymentType === AllowedPaymentMethod.Paypal && (
                        <PayPalPayment
                          onApprove={onPaypalApprove}
                          tokenizeDetails={newPayPalResponse}
                        />
                      )}
                    {isApplePayAllowed &&
                      paymentType === AllowedPaymentMethod.ApplePay &&
                      checkoutSession && (
                        <ApplePayPayment
                          onApprove={onApplePayApprove}
                          tokenizeDetails={newApplePayResponse}
                          checkoutSession={checkoutSession}
                        />
                      )}
                  </StyledNewPaymentForm>
                )}

                {isInvoiceUser && <PoNumberInput />}

                <Button
                  disabled={isContinueButtonDisabled(valid)}
                  htmlType="submit"
                  loading={isLoading}
                >
                  {continueToReviewButtonText}
                </Button>
              </>
            ) : (
              <StyledSpinner dotSize="16px" />
            )}
          </StyledFieldSet>
        </form>
      )}
    />
  );
};
